import dayjs from "dayjs";
import React from "react";
import {
CONTRIBUTION_STATUS_IN_DEBT_COLLECTION,
CONTRIBUTION_STATUS_RESERVED
} from "../../../app/constants/contributionCalendarStatus";
import { OPEN_ITEM_STATUS_OPEN } from "../../../app/constants/openItemStatus";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly } from "../../../app/units/format";
import { formatDateOnlyIso } from "../../../app/utils/formatDateOnlyIso";
import type { UnitsSettings } from "../../../app/units/units";
import { PaymentDrawer,type PaymentItem } from "../../../components/PaymentDrawer";
import type { ContributionCalendarRow,OpenItemRow } from "./personDetailTypes";
import { isContributionWaived } from "./personDetailUtils";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";
import { MIN_PAYABLE_AMOUNT } from "./personAmountThresholds";

type ApiClient = {
  get: <T>(url: string) => Promise<T>;
};

type PaymentDrawerState = {
  open: boolean;
  items: PaymentItem[];
  selectedItems: PaymentItem[];
  setOpen: (open: boolean) => void;
  setSelectedItems: React.Dispatch<React.SetStateAction<PaymentItem[]>>;
};

type Props = {
  canWriteFinance: boolean;
  api: ApiClient;
  text: I18nContextValue["text"];
  personId: string;
  studioId?: string | null;
  openItemsStudioIdsParam?: string;
  units: UnitsSettings;
  paymentsState: PaymentDrawerState;
  contributionsState: PaymentDrawerState;
  openItemsState: PaymentDrawerState;
  onReloadPayments: () => void;
  onReloadContributions: () => void;
  onReloadWallets: () => void;
  onReloadOpenItems: () => void;
};

function openItemsUrl(personId: string, openItemsStudioIdsParam?: string) {
  const q = new URLSearchParams();
  q.set("personId", personId);
  if (openItemsStudioIdsParam) q.set("studioIds", openItemsStudioIdsParam);
  return `/api/v1/open-items?${q.toString()}`;
}

function mapPayableOpenItem(oi: OpenItemRow, text: I18nContextValue["text"], todayIso: string): PaymentItem | null {
  const isOpen = Number(oi.status || 0) === OPEN_ITEM_STATUS_OPEN;
  const isInSepaRun = oi.submittedSepaDirectDebitRunId != null && oi.submittedSepaDirectDebitRunId !== "";
  const openAmount = Number(oi.openAmount ?? (Number(oi.amount || 0) - Number(oi.paidAmount || 0)));
  // Im eingereichten Lastschriftlauf oder mit laufender Online-Zahlung: kassieren hieße doppelt einziehen.
  if (!isOpen || isInSepaRun || oi.hasPendingOnlinePayment === true || openAmount <= MIN_PAYABLE_AMOUNT) return null;
  const dueDate = String(oi.dueDate || "").slice(0, ISO_DATE_LENGTH);

  return {
    // Fällige Posten sind der Vorschlag beim Kassieren; künftige wählt man bei Bedarf dazu.
    suggested: dueDate !== "" && dueDate <= todayIso,
    id: oi.id,
    description: oi.description || text("Open item"),
    dueDate,
    openAmount,
    paymentMethod: oi.paymentMethod ?? null,
    type: "openItem"
  };
}

const openItemPaymentEndpoint = (itemId: string) => `/api/v1/open-items/${encodeURIComponent(itemId)}/pay`;

type OpenItemPaymentDrawerProps = {
  api: ApiClient;
  text: I18nContextValue["text"];
  personId: string;
  studioId?: string | null;
  openItemsStudioIdsParam?: string;
  items?: PaymentItem[];
  selectedItems: PaymentItem[];
  onSelectedItemsChange: (items: PaymentItem[]) => void;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * Zahlung auf offene Posten einer Person erfassen — derselbe Drawer aus Personenakte (Vorgangsleiste,
 * Reiter Zahlungen und offene Posten) und Check-in. Zahlbar ist, was offen und nicht in einem
 * Lastschriftlauf eingereicht ist.
 */
export function PersonOpenItemPaymentDrawer({
  api,
  text,
  personId,
  studioId,
  openItemsStudioIdsParam,
  items,
  selectedItems,
  onSelectedItemsChange,
  onClose,
  onSuccess
}: OpenItemPaymentDrawerProps) {
  const fetchOpenItems = async () => {
    const openItemsList = await api.get<OpenItemRow[]>(openItemsUrl(personId, openItemsStudioIdsParam));
    const todayIso = formatDateOnlyIso(dayjs()) ?? "";
    return (openItemsList || [])
      .map((oi) => mapPayableOpenItem(oi, text, todayIso))
      .filter((item): item is PaymentItem => item !== null);
  };
  return (
    <PaymentDrawer
      open
      title={text("Record payment")}
      onClose={onClose}
      onSuccess={onSuccess}
      items={items ?? []}
      selectedItems={selectedItems}
      onSelectedItemsChange={onSelectedItemsChange}
      paymentEndpoint={openItemPaymentEndpoint}
      fetchAvailableItems={fetchOpenItems}
      preselectSuggestedItems
      itemType="openItem"
      personId={personId}
      studioId={studioId ?? undefined}
    />
  );
}

export function PersonPaymentRecordDrawers({
  canWriteFinance,
  api,
  text,
  personId,
  studioId,
  openItemsStudioIdsParam,
  units,
  paymentsState,
  contributionsState,
  openItemsState,
  onReloadPayments,
  onReloadContributions,
  onReloadWallets,
  onReloadOpenItems
}: Props) {
  if (!canWriteFinance) return null;

  const closeDrawer = (state: PaymentDrawerState) => {
    state.setOpen(false);
    state.setSelectedItems([]);
  };

  const reloadAll = () => {
    onReloadPayments();
    onReloadContributions();
    onReloadWallets();
    onReloadOpenItems();
  };

  const openItemDrawer = (state: PaymentDrawerState) => (
    <PersonOpenItemPaymentDrawer
      api={api}
      text={text}
      personId={personId}
      studioId={studioId}
      openItemsStudioIdsParam={openItemsStudioIdsParam}
      items={state.items}
      selectedItems={state.selectedItems}
      onSelectedItemsChange={state.setSelectedItems}
      onClose={() => closeDrawer(state)}
      onSuccess={reloadAll}
    />
  );

  return (
    <>
      {paymentsState.open ? openItemDrawer(paymentsState) : null}

      {contributionsState.open ? (
        <PaymentDrawer
          open
          title={text("Record payment")}
          onClose={() => closeDrawer(contributionsState)}
          onSuccess={reloadAll}
          items={contributionsState.items}
          selectedItems={contributionsState.selectedItems}
          onSelectedItemsChange={contributionsState.setSelectedItems}
          paymentEndpoint={openItemPaymentEndpoint}
          fetchAvailableItems={async () => {
            const list = await api.get<ContributionCalendarRow[]>(`/api/v1/contribution-calendar?personId=${encodeURIComponent(personId)}`);
            return (list || [])
              .filter((r) =>
                !isContributionWaived(r)
                && !(Number(r.status || 0) === CONTRIBUTION_STATUS_RESERVED)
                && !(Number(r.status || 0) === CONTRIBUTION_STATUS_IN_DEBT_COLLECTION)
                && Number(r.openAmount || 0) > MIN_PAYABLE_AMOUNT
              )
              .map((r) => ({
                id: r.openItemId || r.id,
                openItemId: r.openItemId || null,
                description: String(r.contractName || r.contractPlanName || "").trim() || `${text("Contribution")} ${formatDateOnly(String(r.dueDate || "").slice(0, ISO_DATE_LENGTH), units)}`,
                dueDate: String(r.dueDate || "").slice(0, ISO_DATE_LENGTH),
                openAmount: Number(r.openAmount || 0),
                paymentMethod: r.paymentMethod ?? null,
                type: "contribution"
              }));
          }}
          personId={personId}
          studioId={studioId ?? undefined}
          itemType="contribution"
        />
      ) : null}

      {openItemsState.open ? openItemDrawer(openItemsState) : null}
    </>
  );
}
